import React from 'react';
import MarkdownText from 'MarkdownText';

// Card for `kind: "tobin.menu"`. Renders a header (restaurant name) plus
// one row per dish: thumbnail on the left, name+price+description on the
// right. Images are optional — onError hides the thumbnail and the row
// reflows so the text fills the row.
//
// Expected structured shape:
//   { kind: "tobin.menu",
//     text: "## <Restaurant> — full menu",     // markdown header
//     items: [
//       { name, price, description?, image_url?, action? },
//       …
//     ] }
// When an item carries an `action`, its row is tappable (keyboard-accessible)
// and fires onAction(item.action, structured) — used to open the dish's detail
// card. Rows without an action render as plain, non-interactive rows.
export default function TobinMenuCard({ structured, onAction }) {
  const items = structured.items || [];
  const actions = structured.actions || [];
  return (
    <div style={{
      marginBottom: '10px', padding: '12px', background: '#f8f9fa',
      borderRadius: '8px', border: '1px solid #e9ecef',
    }}>
      {structured.text && (
        <div style={{ fontSize: '0.95rem', marginBottom: '12px' }}>
          <MarkdownText text={structured.text} />
        </div>
      )}
      {actions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {actions.map((action, i) => (
            <button
              key={i}
              onClick={() => onAction(action, structured)}
              style={{
                fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px',
                border: '1px solid #0084ff', borderRadius: '14px',
                background: 'transparent', color: '#0084ff', cursor: 'pointer',
              }}
            >
              {action.label || 'Action'}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {items.map((item, i) => {
          const clickable = !!item.action;
          const fire = clickable ? () => onAction(item.action, structured) : undefined;
          return (
          <div
            key={i}
            onClick={fire}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(); }
            } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            title={clickable ? `See ${item.name}` : undefined}
            onMouseEnter={clickable ? (e) => {
              e.currentTarget.style.background = '#f0f7ff';
              e.currentTarget.style.borderColor = '#bcdcff';
            } : undefined}
            onMouseLeave={clickable ? (e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#f0f0f0';
            } : undefined}
            style={{
              display: 'flex', gap: '10px', alignItems: 'flex-start',
              padding: '8px', background: '#ffffff',
              borderRadius: '6px', border: '1px solid #f0f0f0',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'background 120ms, border-color 120ms',
            }}
          >
            {item.image_url && (
              <div style={{ flex: '0 0 64px', width: '64px', height: '64px' }}>
                <img
                  src={item.image_url}
                  alt=""
                  style={{
                    width: '64px', height: '64px', objectFit: 'cover',
                    borderRadius: '4px', display: 'block',
                  }}
                  onError={(e) => {
                    const wrapper = e.currentTarget.parentElement;
                    if (wrapper) wrapper.style.display = 'none';
                  }}
                />
              </div>
            )}
            <div style={{ flex: '1 1 0', minWidth: 0, fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 700, marginBottom: '2px' }}>
                {item.name}
                {item.price && (
                  <span style={{ float: 'right', fontWeight: 500, color: '#666' }}>
                    {item.price}
                  </span>
                )}
              </div>
              {item.description && (
                <div style={{ color: '#444', lineHeight: 1.4 }}>
                  {item.description}
                </div>
              )}
            </div>
            {clickable && (
              <div style={{
                flex: '0 0 auto', alignSelf: 'center', color: '#0084ff',
                fontSize: '1.1rem', fontWeight: 700, lineHeight: 1,
              }}>
                ›
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
